import React from 'react';
import { Instagram } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const artistsData = [
  {
    name: 'DZERALDAS JERRY KUDREVICIUS',
    fullName: 'Dzeraldas Jerry Kudrevicius',
    image: 'https://res.cloudinary.com/ddclgpokn/image/upload/v1791476522/Geometric_tattoo_artist_newquay_cornwall_best_rated.jpg',
    specialty: 'Owner | Specializes in geometry-dotwork, ornamental, floral, and Oriental Japanese designs.',
    bio: 'Owner of the studio and a tattoo artist with over 22 years of experience, Jerry brings great skill and creativity to every piece he creates. While he\'s well-versed in all styles, his true passion lies in geometry-dotwork, ornamental, floral, and vibrant Oriental Japanese designs. Jerry loves doing animals, often blending them with his signature ornamental elements to craft truly one-of-a-kind tattoos. With around 50 UK and international tattoo conventions under his belt — and multiple award wins for his artistry — Jerry continues to push boundaries. Jerry\'s right hand is his wife Kristina, who serves as the studio receptionist and takes care of everything behind the scenes, ensuring smooth operations and excellent client service.',
    instagram: 'https://www.instagram.com/atlanticcoasttattoo',
    availableDesigns: 'https://www.instagram.com/availabledesigns_jerry',
    email: 'studio@atlanticcoasttattoo.com'
  },
  {
    name: 'JONI SMITH',
    fullName: 'Joni Smith',
    image: 'https://res.cloudinary.com/ddclgpokn/image/upload/v1776090657/1689842F-EAEE-41FE-ABC6-8B63A5A1FB3C_m5qkkf.png',
    specialty: 'Specializes in Japanese, Neo-Traditional, Traditional, and Black & Grey styles.',
    bio: 'Joni Smith is an experienced tattoo artist with nine years in the industry, specialising in Japanese, Neo-Traditional, Traditional, and Black & Grey styles. Originally from Oxfordshire, his work is known for bold compositions, solid linework, and a love of bright, vibrant colour. Joni focuses on creating clean, timeless tattoos, working closely with each client to deliver designs that are both personal and built to last.',
    instagram: 'https://www.instagram.com/joni.smith.tattoo',
    email: 'jonismithtattoo@gmail.com'
  },
  {
    name: 'JOSIE HANDSCOMB',
    fullName: 'Josie Handscomb',
    image: 'https://res.cloudinary.com/ddclgpokn/image/upload/v1776080607/IMG_3108_zrxtal.jpg',
    specialty: 'Specializes in ornamental, illustrative, and traditional tattoos.',
    bio: 'A rising star in the tattoo world with 3 years of experience and a natural talent for turning ideas into beautiful ink. She loves working on ornamental, illustrative, and traditional tattoos, and brings creativity and precision to every piece she creates. Josie is also a versatile artist, often taking on smaller custom tattoos — perfect for walk-ins and those spontaneous ideas.',
    instagram: 'https://www.instagram.com/j.inks.tattoo',
    email: 'josiehandscomb12@gmail.com'
  }
];

const Artists = () => {
  const navigate = useNavigate();
  
  return (
    <section id="artists" className="relative py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-display text-4xl md:text-6xl font-bold text-white">
            ARTISTS
          </h2>
        </div>

        <div className="space-y-20">
          {artistsData.map((artist, index) => (
            <div
              key={artist.name}
              className={`grid md:grid-cols-2 gap-8 items-center ${
                index % 2 === 1 ? 'md:grid-flow-dense' : ''
              }`}
            >
              {/* Image */}
              <div
                className={`relative h-96 overflow-hidden ${
                  index % 2 === 1 ? 'md:col-start-2' : ''
                }`}
              >
                <img
                  src={artist.image}
                  alt={`${artist.fullName} - Tattoo Artist at Atlantic Coast Tattoo specializing in ${artist.specialty}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                
                {/* Add "View Portfolio" button for each artist */}
                <button
                  onClick={() => {
                    if (artist.name === 'DZERALDAS JERRY KUDREVICIUS') navigate('/jerry-gallery');
                    else if (artist.name === 'JONI SMITH') navigate('/joni-gallery');
                    else if (artist.name === 'JOSIE HANDSCOMB') navigate('/josie-gallery');
                  }}
                  className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-white text-black px-6 py-3 text-sm tracking-widest hover:bg-gray-200 transition-colors font-bold z-10"
                >
                  VIEW PORTFOLIO
                </button>
              </div>

              {/* Info */}
              <div className={index % 2 === 1 ? 'md:col-start-1 md:row-start-1' : ''}>
                <div className="flex flex-wrap items-center gap-4 mb-5" data-testid={`artist-links-${index}`}>
                  <a
                    href={artist.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors text-sm tracking-wider"
                  >
                    <Instagram size={20} />
                    INSTAGRAM
                  </a>
                  {artist.availableDesigns && (
                    <a
                      href={artist.availableDesigns}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-white/10 px-4 py-2 text-white hover:bg-white/20 transition-colors text-sm tracking-wider border border-white/30"
                    >
                      <Instagram size={20} />
                      TATTOO IDEAS
                    </a>
                  )}
                  <a
                    href={`mailto:${artist.email}`}
                    className="text-white hover:text-gray-300 transition-colors text-sm tracking-wider"
                  >
                    EMAIL
                  </a>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white tracking-widest mb-2">
                  {artist.name}
                </h3>
                <p className="text-white/80 text-sm tracking-wider mb-4">
                  {artist.specialty}
                </p>
                <p className="text-white/70 leading-relaxed">
                  {artist.bio}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Tattoo Artist Wanted */}
        <div
          data-testid="artist-wanted-section"
          className="mt-24 border border-white/15 bg-white/[0.03]"
        >
          <div className="grid md:grid-cols-2">
            <div className="relative h-72 md:h-auto overflow-hidden">
              <img
                src="https://res.cloudinary.com/ddclgpokn/image/upload/v1790057203/Tattoo-artist-job-position-newquay-Cornwall-uk.jpg"
                alt="Tattoo Artist Wanted at Atlantic Coast Tattoo, Newquay, Cornwall UK"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                data-testid="artist-wanted-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent md:bg-gradient-to-r"></div>
            </div>

            <div className="p-8 md:p-12">
              <h3
                className="text-3xl md:text-4xl font-bold text-white tracking-widest mb-6"
                data-testid="artist-wanted-heading"
              >
                TATTOO ARTIST WANTED
              </h3>
              <p className="text-white/80 leading-relaxed mb-4">
                Join the team at Atlantic Coast Tattoo, Newquay.
              </p>
              <p className="text-white/70 leading-relaxed mb-4">
                We're looking for an experienced tattoo artist to join our busy, established studio on the Cornish Atlantic coast.
              </p>
              <p className="text-white/70 leading-relaxed mb-4">
                We're open to full or part-time artists with a strong portfolio, whether you're versatile or have a distinctive style that adds something new to our team.
              </p>
              <p className="text-white font-semibold tracking-wider mb-4">
                Busy walk-in trade • Custom work • Great team • Prime Newquay location
              </p>
              <p className="text-white/70 leading-relaxed mb-4">
                If you're professional, creative and confident working in a fast-paced studio, we'd love to see your work.
              </p>
              <p className="text-white/70 leading-relaxed mb-8">
                Send us your portfolio and tell us a little about yourself.
              </p>
              <a
                href="mailto:studio@atlanticcoasttattoo.com?subject=Tattoo Artist Application"
                className="inline-block bg-white text-black px-8 py-3 text-sm tracking-widest hover:bg-gray-200 transition-colors font-bold"
                data-testid="artist-wanted-apply-btn"
              >
                SEND YOUR PORTFOLIO
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Artists;